import { Moon, Sun } from 'lucide-react';
import React, { useEffect } from 'react';
import { Box, useColorMode } from 'theme-ui';

const COLOR_MODE_CLASSES = ['theme-ui-dark', 'theme-ui-light'] as const;

const TRACK_WIDTH = 52;
const TRACK_HEIGHT = 28;
const THUMB_SIZE = 20;
const TRACK_INSET = 2;

const ThemeToggle: React.FC = () => {
  const [colorMode, setColorMode] = useColorMode();
  const isDark = colorMode !== 'light';

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...COLOR_MODE_CLASSES);

    if (colorMode === 'light' || colorMode === 'dark') {
      root.classList.add(`theme-ui-${colorMode}`);
    }
  }, [colorMode]);

  const toggleColorMode = () => {
    setColorMode(isDark ? 'light' : 'dark');
  };

  return (
    <Box
      as="button"
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
      onClick={toggleColorMode}
      sx={{
        boxSizing: 'border-box',
        position: 'relative',
        display: 'block',
        width: TRACK_WIDTH,
        height: TRACK_HEIGHT,
        flexShrink: 0,
        margin: 0,
        verticalAlign: 'middle',
        borderRadius: '999px',
        border: '2px solid',
        borderColor: 'primary',
        backgroundColor: isDark ? 'muted' : 'highlight',
        padding: `${TRACK_INSET}px`,
        cursor: 'pointer',
        transition: 'background-color 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          borderColor: 'secondary',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary',
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: isDark
            ? `calc(100% - ${THUMB_SIZE + TRACK_INSET}px)`
            : `${TRACK_INSET}px`,
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          borderRadius: '50%',
          backgroundColor: 'background',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'shadowSm',
          color: 'primary',
          lineHeight: 0,
          transform: 'translateY(-50%)',
          transition:
            'left 0.25s ease, background-color 0.25s ease, transform 0.25s ease',
          '& svg': {
            display: 'block',
            flexShrink: 0,
          },
        }}
      >
        {isDark ? (
          <Moon size={13} strokeWidth={2} />
        ) : (
          <Sun size={13} strokeWidth={2} />
        )}
      </Box>
    </Box>
  );
};

export default ThemeToggle;
